import { Grievance } from "@/data/grievances";
import { GrievanceUpdate } from "@/data/grievances";
import { addLog } from "@/logger";

/** JSON:API grievance resource from backend */
interface JsonApiGrievanceItem {
  id?: string;
  attributes?: Record<string, unknown> & {
    name?: string;
    created?: string;
    field_case_status?: string;
    field_parish?: string;
    field_reported_by?: string;
    field_category?: string;
    field_impact?: string;
    field_operator?: string;
    field_community?: string;
    notes?: { value?: string };
    drupal_internal__id?: string;
  };
  relationships?: {
    image?: { data?: Array<{ type?: string; id?: string }> };
  };
}

/** JSON:API grievance update (case_event comment) resource */
interface JsonApiUpdateItem {
  id?: string;
  attributes?: { created?: string; comment_body?: { value?: string } };
  relationships?: { entity_id?: { data?: { id?: string } } };
}

/** Form shape for Add Case */
export interface AddCaseFormData {
  title: string;
  parish: string;
  operator?: string;
  community?: string;
  description?: string;
  impact?: string | null;
  reportedBy?: string | null;
  tags?: string[];
  deviceId?: string | null;
}

export const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "Unknown date";

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "Invalid date";
  }
};

export const mapGrievanceInterface = (data: JsonApiGrievanceItem | null | undefined): Grievance => {
  if (!data) {
    addLog("Warning: Empty data in mapGrievanceInterface");
    return {
      id: "",
      title: "",
      date: "",
      description: "",
      status: "pending",
      category: "environmental",
      location: "",
      companyInvolved: "",
      images: [],
      reportedBy: "",
    };
  }

  try {
    // First log the raw data structure to help debugging
    addLog(`Mapping grievance: ${JSON.stringify(data).substring(0, 150)}...`);

    if (data?.attributes) {
      // Extract values with proper null checks
      const attributes = data.attributes;
      const displayId = attributes.drupal_internal__id || "";
      const id = data.id || "";
      const title = attributes.name || "";
      const created = attributes.created ?? "";
      const date = formatDate(created) || "";
      const description = attributes.notes?.value || "";
      const status = String(attributes.field_case_status ?? "investigating");
      const parish = String(attributes.field_parish ?? "");
      const reporter = String(attributes.field_reported_by ?? "");
      const category = String(attributes.field_category ?? "").toLowerCase();
      const impact = String(attributes.field_impact ?? "");
      const operator = String(attributes.field_operator ?? "");
      const community = String(attributes.field_community ?? "");
      // API returns image relationship as { data: [{ id, type: "file--file" }] }. Extract file entity IDs so CaseDetail can fetch URLs via getCaseImageById.
      const imageData = data.relationships?.image?.data;
      const images: string[] = Array.isArray(imageData)
        ? imageData.map((item) => item?.id).filter((id): id is string => Boolean(id))
        : [];

      addLog(`Extracted grievance fields: title=${title}, status=${status}`);

      const grievance: Grievance = {
        displayId,
        id,
        title,
        date,
        createdAt: typeof created === "string" ? created : "",
        description,
        status: (status && String(status)) as Grievance["status"] || "investigating",
        category: (category && ["environmental", "health", "property", "social", "other"].includes(category) ? category : "other") as Grievance["category"],
        location: `${replaceUnderScoreWithSpace(parish)}, ${replaceUnderScoreWithSpace(community)}`,
        companyInvolved: operator,
        images,
        reportedBy: reporter,
        impact,
      };

      return grievance;
    }
    addLog("Warning: Missing attributes in data for mapGrievanceInterface");
    addLog(`Data structure: ${JSON.stringify(Object.keys(data || {}))}`);
    return {
      id: "",
      title: "",
      date: "",
      description: "",
      status: "pending",
      category: "environmental",
      location: "",
      companyInvolved: "",
      images: [],
      reportedBy: "",
    };
  } catch (error) {
    addLog(
      "Error in mapGrievanceInterface: " +
        (error instanceof Error ? error.message : String(error))
    );
    return {
      id: "",
      title: "",
      date: "",
      description: "",
      status: "pending",
      category: "environmental",
      location: "",
      companyInvolved: "",
      images: [],
      reportedBy: "",
      community: "",
    };
  }
};

// Helper to transform API response for the entire collection
export const mapGrievanceCollection = (response: { data?: JsonApiGrievanceItem | JsonApiGrievanceItem[] } | null): Grievance[] => {
  if (!response) {
    addLog("Error: Null response in mapGrievanceCollection");
    return [];
  }

  try {
    // Log the raw response structure first
    addLog(
      `Raw response structure: ${JSON.stringify(Object.keys(response || {}))}`
    );

    if (!response.data || !Array.isArray(response.data)) {
      addLog("Invalid API response format for grievance collection");
      if (
        response.data &&
        typeof response.data === "object" &&
        !Array.isArray(response.data)
      ) {
        addLog("Detected single item response, converting to array");
        return [mapGrievanceInterface(response.data)];
      }

      return [];
    }

    addLog(`Found ${response.data.length} grievances in response`);

    const mappedGrievances = response.data
      .filter((item: JsonApiGrievanceItem | null | undefined) => item !== null && item !== undefined)
      .map((item: JsonApiGrievanceItem) => {
        try {
          return mapGrievanceInterface(item);
        } catch (err) {
          addLog(
            `Error mapping individual grievance: ${
              err instanceof Error ? err.message : String(err)
            }`
          );
          return null;
        }
      })
      .filter((item: Grievance | null): item is Grievance => item !== null);

    addLog(`Successfully mapped ${mappedGrievances.length} grievances`);
    return mappedGrievances;
  } catch (error) {
    addLog(
      "Error in mapGrievanceCollection: " +
        (error instanceof Error ? error.message : String(error))
    );
    return [];
  }
};

export const mapGrievanceUpdate = (data: JsonApiUpdateItem | null | undefined): GrievanceUpdate => {
  if (!data) {
    addLog("Warning: Empty data in mapGrievanceUpdate");
    return { id: "", date: "", content: "", caseId: "" };
  }

  try {
    addLog(
      `Mapping grievance update: ${JSON.stringify(data).substring(0, 150)}...`
    );

    const id = data.id || "";
    const date = data.attributes?.created || "";
    const content = data.attributes?.comment_body?.value || "";
    const caseId = data.relationships?.entity_id.data.id || "";

    return { id, date, content, caseId };
  } catch (error) {
    addLog(
      "Error in mapGrievanceUpdate: " +
        (error instanceof Error ? error.message : String(error))
    );
    return { id: "", date: "", content: "", caseId: "" };
  }
};

export const mapGrievanceUpdateCollection = (
  response: { data?: JsonApiUpdateItem | JsonApiUpdateItem[] } | null
): GrievanceUpdate[] => {
  if (!response) {
    addLog("Error: Null response in mapGrievanceUpdateCollection");
    return [];
  }

  try {
    addLog(
      `Raw update response structure: ${JSON.stringify(
        Object.keys(response || {})
      )}`
    );

    if (!response.data || !Array.isArray(response.data)) {
      addLog("Invalid API response format for grievance update collection");

      // Handle single item
      if (
        response.data &&
        typeof response.data === "object" &&
        !Array.isArray(response.data)
      ) {
        addLog("Detected single update response, converting to array");
        return [mapGrievanceUpdate(response.data)];
      }

      return [];
    }

    addLog(`Found ${response.data.length} grievance updates in response`);

    const mappedUpdates = response.data
      .filter((item: JsonApiUpdateItem | null | undefined) => item !== null && item !== undefined)
      .map((item: JsonApiUpdateItem) => {
        try {
          return mapGrievanceUpdate(item);
        } catch (err) {
          addLog(
            `Error mapping individual grievance update: ${
              err instanceof Error ? err.message : String(err)
            }`
          );
          return null;
        }
      })
      .filter((item: GrievanceUpdate | null): item is GrievanceUpdate => item !== null);

    addLog(`Successfully mapped ${mappedUpdates.length} grievance updates`);
    return mappedUpdates;
  } catch (error) {
    addLog(
      "Error in mapGrievanceUpdateCollection: " +
        (error instanceof Error ? error.message : String(error))
    );
    return [];
  }
};

export const buildGrievanceFilter = (filterName: string, field: string, value: string) => {
  if (!value) {
    addLog("Error: no value submitted for filter ");
    return;
  }

  return `filter[${filterName}][condition][path]=${field}&filter[${filterName}][condition][value]=${value}`;
};

export const handleCaseObjectUpdate = (propertyPath: string, value: unknown): Record<string, unknown> => {
  const updatedObject: Record<string, unknown> = {};
  let target: Record<string, unknown> = updatedObject;
  const keys = propertyPath.split(".");
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      target[key] = value;
    } else {
      if (!target[key]) target[key] = {};
      target = target[key] as Record<string, unknown>;
    }
  });
  return updatedObject;
};

function replaceSpaceWithUnderscore(input: string): string {
  if (input.includes(" ")) {
    return input.replace(/ /g, "_");
  }
  return input;
}

function replaceUnderScoreWithSpace(input: unknown): string {
  const s = input == null ? "" : String(input);
  if (s.includes("_")) {
    const parish = s.replace(/_/g, " ");
    const arr = parish.split(" ");
    return (arr[0] ? capitalizeFirstLetter(arr[0]) : "") + (arr[1] ? " " + capitalizeFirstLetter(arr[1]) : "");
  }
  if (s.length !== 0) return capitalizeFirstLetter(s);
  return s;
}

function capitalizeFirstLetter(val: unknown): string {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

export function transformFormToApiAttributes(formData: AddCaseFormData) {
  if (!formData || !formData.title || !formData.parish) {
    throw new Error("Missing required form fields: title and parish.");
  }

  return {
    data: {
      type: "log--activity",
      attributes: {
        name: formData.title,
        field_parish: replaceSpaceWithUnderscore(formData.parish.toLowerCase()),
        // Backend requires field_operator; send "Other" when empty to avoid validation error
        field_operator: formData.operator?.trim() || "Other",
        field_community: formData.community || null,
        field_impact: formData.impact || null,
        field_reported_by: formData.reportedBy || null,
        field_device_id: formData.deviceId || null,
        tags: Array.isArray(formData.tags) ? formData.tags : [],
        field_case_status: "pending",
        flag: ["review"],
        timestamp: Math.floor(Date.now() / 1000),
        // text_long requires both value and format for JSON:API validation
        notes: {
          value: formData.description ?? "",
          format: "plain_text",
        },
      },
    },
  };
}
